import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert";
import { DOMAIN } from "../../strings/CONSTANTS";

const ManageStickerPacks: React.FC = () => {
  const [packs, setPacks] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [filteredCategory, setFilteredCategory] = useState("");

  // -------------------------------------------------------
  // Fetch Categories
  // -------------------------------------------------------
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${DOMAIN}/category/list`);
      setCategories(res.data.data || []);
    } catch (err) {
      console.log("Category fetch error:", err);
    }
  };

  // -------------------------------------------------------
  // Fetch Sticker Packs
  // -------------------------------------------------------
  const fetchPacks = async () => {
    try {
      const res = await axios.get(`${DOMAIN}/sticker/list`);
      setPacks(res.data.packs || []);
    } catch (err) {
      console.log("Sticker packs fetch error:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchPacks();
  }, []);

  // -------------------------------------------------------
  // Delete Sticker Pack
  // -------------------------------------------------------
  const deletePack = async (id: string) => {
    const confirmDelete = window.confirm("Delete this sticker pack?");
    if (!confirmDelete) return;

    try {
      const res = await axios.delete(`${DOMAIN}/sticker/delete/${id}`);
      if (res.data.success) {
        Swal("Deleted", "Sticker pack removed", "success");
        fetchPacks();
      }
    } catch {
      Swal("Error", "Unable to delete pack", "error");
    }
  };

  // -------------------------------------------------------
  // Filter Packs
  // -------------------------------------------------------
  const filteredPacks =
    filteredCategory === ""
      ? packs
      : packs.filter((p: any) => p.categoryId === filteredCategory);

  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold">Manage Sticker Packs</h1>

      {/* CATEGORY FILTER */}
      <div className="mt-4 mb-6 w-[300px]">
        <label className="text-sm font-medium">Filter by Category</label>
        <select
          className="border p-2 w-full rounded"
          value={filteredCategory}
          onChange={(e) => setFilteredCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map((cat: any) => (
            <option key={cat._id} value={cat._id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Sticker Packs List */}
      <div className="grid grid-cols-3 gap-6">
        {filteredPacks.length === 0 && (
          <p className="text-gray-500 col-span-3">
            No sticker packs found for this category.
          </p>
        )}

        {filteredPacks.map((pack: any) => (
          <div key={pack._id} className="border p-4 bg-white rounded shadow-sm">
            {/* Badge Row - NEW */}
            <div className="flex justify-center gap-2 mb-2 flex-wrap">
              {pack.type === "animated" && (
                <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                  🎬 Animated
                </span>
              )}
              {pack.isPremium && (
                <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                  👑 Premium
                </span>
              )}
              {pack.isFeatured && (
                <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                  ⭐ Featured
                </span>
              )}
            </div>

            {/* Main Image */}
            <img
              src={pack.mainImage}
              alt={pack.title}
              className="w-24 h-24 mx-auto object-cover rounded mb-3"
            />

            {/* Title */}
            <h3 className="font-semibold text-center">{pack.title}</h3>

            {/* Category */}
            <p className="text-sm text-gray-600 text-center mb-3">
              {pack.category?.name || "No Category"}
            </p>

            {/* Stickers Preview */}
            <div>
              <p className="text-sm font-medium mb-1">Stickers:</p>
              <div className="grid grid-cols-5 gap-2">
                {pack.stickers?.slice(0, 5).map((img: string, idx: number) => (
                  <img
                    key={idx}
                    src={img}
                    alt="sticker"
                    className="w-12 h-12 rounded object-cover border"
                  />
                ))}
              </div>
            </div>

            {/* Delete Button */}
            <button
              onClick={() => deletePack(pack._id)}
              className="bg-red-600 text-white px-3 py-1 rounded text-sm mt-4 w-full"
            >
              Delete Pack
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManageStickerPacks;
